/**
 * Casas erguidas no mapa (techspec-casas §03). A casa não está no Tiled: é um multi do UO, lido de
 * `public/casas/multis.json` (gerado por `tools/uo-multis.py`) e posto no lote pelo servidor (`ox, oy` = centro do multi).
 *
 * - **Desenho:** as peças entram na lista de desenho do motor na ordem do cliente do UO: diagonal, depois prioridade de z
 *   (`fracDePrio`). Os corpos usam a mesma régua (prio = z do pé + 2), então o boneco do 1º andar fica na frente do piso
 *   dele e atrás do telhado.
 * - **Teto:** com o jogador dentro da casa e acima do terreno, some tudo com z ≥ z do pé + 15, como no cliente.
 * - **Porta:** some (abre) com o jogador a uma casa dela. A trava do lote é do servidor.
 * - **Andar (fatia B, §03.10):** a tabela `andares` diz em que alturas se fica em pé em cada casa da planta e para onde
 *   cada passo leva (escada, 1º andar, terraço). O servidor anda pela MESMA tabela.
 */
import { Z_PX } from './blocos';
import type { MapaCarregado } from './mapa';
import { iconePara } from '../assets/items';
import { ACIMA_DA_CABECA, ALTURA_DO_BAU, ALTURA_DO_MURO, alturaDe, arteDoGiro, arteNoInstante, cruzam, ehMobilia, girosDe, giroValido, mobiliaDe, BAU_COMUM } from '../data/mobiliaInfo';
import type { LuzNoMapa } from './mapa';
import { t } from '../i18n/idioma';
import { tocarUo } from '../audio/mixer';
import { PORTA } from '../audio/sonsUo';

type Ctx2D = CanvasRenderingContext2D;

interface DefPeca {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly ax: number;
  readonly ay: number;
  /** A arte do cliente do UO (o `uo-multis.py` grava): a placa da casa é `0BD0`/`0BD2`. */
  readonly id?: number;
}

/** A placa da casa no multi (techspec-casa-interface-e-baus §09): madeira e latão. */
const ARTES_DA_PLACA = new Set([0x0bd0, 0x0bd2]);

interface Andares {
  readonly x0: number;
  readonly y0: number;
  readonly w: number;
  readonly h: number;
  /** Por casa da planta (com a volta de terreno): as alturas em que se fica em pé, em ordem. */
  readonly n: readonly (readonly number[])[];
  /** Por casa e por pé: 8 caracteres (direções do jogo); dígito = pé da vizinha, '-' bloqueado, '*' sai da planta. */
  readonly t: readonly (readonly string[])[];
}

interface ModeloMulti {
  readonly x0: number;
  readonly y0: number;
  readonly w: number;
  readonly h: number;
  readonly piso: number;
  /** [def, dx, dy, z, prio, tipo] na ordem do cliente do UO. */
  readonly pecas: readonly (readonly number[])[];
  readonly andares: Andares;
}

interface CatalogoMultis {
  readonly defs: readonly DefPeca[];
  readonly modelos: Readonly<Record<string, ModeloMulti>>;
  readonly imagem: HTMLImageElement;
  /** Móveis fora dos multis (techspec-casas §04): `bau` = [giro 0, giro 1]. */
  readonly extras: Readonly<Record<string, readonly number[]>>;
  /** Porta fechada (def) → [def aberta, dx, dy, metal]: a arte aberta do UO e para onde ela anda. */
  readonly portas: ReadonlyMap<number, readonly [number, number, number, boolean]>;
}

export interface CasaErguidaNoMapa {
  readonly id: string;
  readonly modelo: string;
  readonly ox: number;
  readonly oy: number;
  /** `bloqueia`: a peça bloqueia o passo (o servidor manda pela config do console). */
  readonly presos?: readonly { readonly defId: string; readonly x: number; readonly y: number; readonly z: number; readonly giro?: number; readonly bloqueia?: boolean }[];
  readonly baus?: readonly { readonly x: number; readonly y: number; readonly z: number; readonly giro: number; readonly defId?: string }[];
  /** O papel do jogador nesta casa (techspec-casas §03.13). */
  readonly papel?: string;
  readonly aberta?: boolean;
  /** A placa (techspec-casa-interface-e-baus §09): o nome que o dono deu e o personagem dono. */
  readonly nome?: string | null;
  readonly dono?: string | null;
}

export interface PecaPosta {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly def: DefPeca;
  readonly prio: number;
  readonly porta: boolean;
  /** Item preso (techspec-casas §04): desenha o ícone do item em vez da arte do atlas. */
  readonly icone?: string;
  /** Mobília (techspec-marceneiro-e-oficios-em-casa §04): o giro da arte e o piso de onde a peça subiu (o teto corta por ele). */
  readonly giro?: number;
  readonly zBase?: number;
  /** Porta (techspec-casas §03.14): a arte aberta e o passo que ela dá ao abrir; `chave` identifica a porta. */
  readonly aberta?: { readonly def: DefPeca; readonly dx: number; readonly dy: number; readonly metal: boolean; readonly chave: string };
}

interface CasaPosta {
  readonly id: string;
  readonly m: ModeloMulti;
  /** Canto da planta do multi no mapa. */
  readonly gx: number;
  readonly gy: number;
  /** Canto da planta com a volta de terreno (a tabela de andares). */
  readonly ax: number;
  readonly ay: number;
  readonly pecas: readonly PecaPosta[];
  /** Baús postos: bloqueiam o passo (`x,y,z`). */
  readonly baus: ReadonlySet<string>;
  /** O papel do jogador nesta casa: dono, codono, amigo, convidado, visitante, banido. */
  readonly papel: string;
  /** A placa (§09): onde ela fica no mapa, o nome da casa, o dono e se está aberta. */
  readonly placas: readonly { readonly x: number; readonly y: number; readonly z: number; readonly def: DefPeca }[];
  readonly nome: string | null;
  readonly dono: string | null;
  readonly aberta: boolean;
  /** A porta abre para o jogador (techspec-casas §03.14): tem papel, ou a casa está aberta e ele não é banido. */
  readonly abrePorta: boolean;
  /** As casas do mapa com peça do multi (x, y em pares), para o minimapa (techspec-casas §03.15). */
  readonly mini: readonly number[];
  /** Baús e itens presos, para o clique e para não pôr duas coisas no mesmo lugar (`x,y,z`). */
  readonly props: ReadonlyMap<string, 'bau' | 'preso'>;
  /** O que ocupa cada lugar, com a altura (§04.2): a vela cabe em cima da mesa; a cadeira não cabe na cadeira. */
  readonly ocupa: readonly { readonly x: number; readonly y: number; readonly z: number; readonly altura: number; readonly sup: boolean; readonly bloqueia?: boolean }[];
  /** Caixa da arte em pixels de mundo (para pular a casa fora da tela). */
  readonly px0: number;
  readonly py0: number;
  readonly px1: number;
  readonly py1: number;
}

const TIPO_PORTA = 2;
/** A caixa do ícone de item preso, em pixels de mundo: no meio do losango da casa. */
const ICONE_LADO = 30;
const ICONE_DEF: DefPeca = { x: 0, y: 0, w: ICONE_LADO, h: ICONE_LADO, ax: ICONE_LADO / 2, ay: ICONE_LADO / 2 + 22 };
const icones = new Map<string, HTMLImageElement>();

function iconeDe(defId: string): HTMLImageElement | null {
  let img = icones.get(defId);
  if (!img) {
    img = new Image();
    img.decoding = 'async';
    img.src = iconePara(defId);
    icones.set(defId, img);
  }
  return img.complete && img.naturalWidth > 0 ? img : null;
}
const TW = 44;
const TH = 44;
const ID_OK = /^[a-z0-9-]+$/;
/** As direções do jogo (DIRS do motor, CityDirs do servidor). */
const DIRS: readonly (readonly [number, number])[] = [[1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1]];

let carga: Promise<CatalogoMultis | null> | null = null;
let catalogo: CatalogoMultis | null = null;
let postas: { mapaId: string; casas: readonly CasaPosta[] } = { mapaId: '', casas: [] };

function inteiro(n: unknown): n is number {
  return typeof n === 'number' && Number.isInteger(n);
}

/**
 * A régua de desenho dentro da diagonal (techspec-casas §03.10): prio → fração em [0.001, 0.82]. Peça usa a prio do UO;
 * corpo usa z do pé + 2. Fica acima do `x + y + z` inteiro dos estáticos do mapa, como o corpo sempre ficou.
 */
export function fracDePrio(prio: number): number {
  return (Math.max(-19, Math.min(800, prio)) + 20) / 1000;
}

function andaresValidos(a: Andares | undefined): boolean {
  if (!a || ![a.x0, a.y0, a.w, a.h].every(inteiro) || a.w <= 0 || a.h <= 0 || a.w > 66 || a.h > 66) return false;
  if (!Array.isArray(a.n) || !Array.isArray(a.t) || a.n.length !== a.w * a.h || a.t.length !== a.w * a.h) return false;
  return a.n.every((pes, i) => Array.isArray(pes) && pes.every(inteiro) && pes.length <= 10
    && Array.isArray(a.t[i]) && a.t[i].length === pes.length && a.t[i].every(s => /^[0-9*-]{8}$/.test(s)));
}

/** Lê e confere o catálogo (A08: arquivo torto não vira laço nem índice fora da faixa). */
function validar(raw: unknown): Omit<CatalogoMultis, 'imagem'> & { atlas: string; versao: string } {
  const r = raw as {
    formato?: number; atlas?: string; versao?: string; defs?: DefPeca[]; modelos?: Record<string, ModeloMulti>;
    extras?: Record<string, number[]>;
    portas?: Record<string, number[]>;
  };
  if (r?.formato !== 1 || typeof r.atlas !== 'string' || !/^[a-z0-9-]+\.png$/.test(r.atlas))
    throw new Error('casas: multis.json com formato desconhecido.');
  const defs = Array.isArray(r.defs) ? r.defs : [];
  for (const d of defs)
    if (![d.x, d.y, d.w, d.h, d.ax, d.ay].every(inteiro)) throw new Error('casas: arte torta no multis.json.');
  const modelos: Record<string, ModeloMulti> = {};
  for (const [id, m] of Object.entries(r.modelos ?? {})) {
    if (!ID_OK.test(id) || ![m.x0, m.y0, m.w, m.h, m.piso].every(inteiro)) continue;
    if (m.w <= 0 || m.h <= 0 || m.w > 64 || m.h > 64 || !andaresValidos(m.andares)) continue;
    if (!m.pecas.every(p => p.length === 6 && p.every(inteiro) && p[0] >= 0 && p[0] < defs.length)) continue;
    modelos[id] = m;
  }
  const extras: Record<string, readonly number[]> = {};
  for (const [nome, ids] of Object.entries(r.extras ?? {}))
    if (ID_OK.test(nome) && Array.isArray(ids) && ids.every(i => inteiro(i) && i >= 0 && i < defs.length)) extras[nome] = ids;
  const portas = new Map<number, readonly [number, number, number, boolean]>();
  for (const [k, v] of Object.entries(r.portas ?? {})) {
    const fechada = Number(k);
    if (!inteiro(fechada) || fechada < 0 || fechada >= defs.length || !Array.isArray(v) || v.length !== 4 || !v.every(inteiro)) continue;
    if (v[0] < 0 || v[0] >= defs.length || Math.abs(v[1]) > 1 || Math.abs(v[2]) > 1) continue;
    portas.set(fechada, [v[0], v[1], v[2], v[3] === 1]);
  }
  return { defs, modelos, extras, portas, atlas: r.atlas, versao: typeof r.versao === 'string' ? r.versao.replace(/[^a-z0-9]/g, '') : '' };
}

/** O catálogo de multis e o atlas, uma vez por sessão. Falha vira `null` (o mapa segue sem casas). */
export function carregarCasas(): Promise<CatalogoMultis | null> {
  carga ??= (async () => {
    try {
      const res = await fetch('/casas/multis.json', { cache: 'no-cache' });
      if (!res.ok) throw new Error(`casas: ${res.status}`);
      const v = validar(await res.json());
      const imagem = new Image();
      imagem.decoding = 'async';
      imagem.src = `/casas/${v.atlas}?v=${v.versao}`;
      await imagem.decode();
      catalogo = { defs: v.defs, modelos: v.modelos, extras: v.extras, portas: v.portas, imagem };
      return catalogo;
    } catch (e) {
      console.warn('casas: catálogo indisponível', e);
      carga = null;
      return null;
    }
  })();
  return carga;
}

/** O centro do multi num lote, pela mesma conta do servidor (`CasaMultis.Origem`). Usado pelo modo offline. */
export async function origemNoLote(
  lote: { x0: number; y0: number; x1: number; y1: number }, modelo: string
): Promise<{ ox: number; oy: number } | null> {
  const cat = await carregarCasas();
  const m = cat?.modelos[modelo];
  if (!m) return null;
  return {
    ox: lote.x0 + Math.trunc((lote.x1 - lote.x0 + 1 - m.w) / 2) - m.x0,
    oy: lote.y0 + Math.trunc((lote.y1 - lote.y0 + 1 - m.h) / 2) - m.y0
  };
}

function montar(cat: CatalogoMultis, c: CasaErguidaNoMapa): CasaPosta | null {
  const m = cat.modelos[c.modelo];
  if (!m || !inteiro(c.ox) || !inteiro(c.oy)) return null;
  const pecas: PecaPosta[] = [];
  let px0 = Infinity, py0 = Infinity, px1 = -Infinity, py1 = -Infinity;
  const vistas = new Set<number>();
  const mini: number[] = [];
  const placas: { x: number; y: number; z: number; def: DefPeca }[] = [];
  for (const [d, dx, dy, z, prio, tipo] of m.pecas) {
    const k = (dy + 128) * 256 + (dx + 128);
    if (!vistas.has(k)) {
      vistas.add(k);
      mini.push(c.ox + dx, c.oy + dy);
    }
    const def = cat.defs[d];
    const x = c.ox + dx, y = c.oy + dy;
    if (def.id !== undefined && ARTES_DA_PLACA.has(def.id)) placas.push({ x, y, z, def });
    const porta = (tipo & TIPO_PORTA) !== 0;
    const ab = porta ? cat.portas.get(d) : undefined;
    pecas.push({
      x, y, z, def, prio, porta,
      ...(ab ? { aberta: { def: cat.defs[ab[0]], dx: ab[1], dy: ab[2], metal: ab[3], chave: `${c.id}:${x},${y},${z}` } } : {})
    });
    const sx = (x - y) * TW / 2 - def.ax;
    const sy = (x + y) * TH / 2 + TH - z * Z_PX - def.ay;
    px0 = Math.min(px0, sx); py0 = Math.min(py0, sy);
    px1 = Math.max(px1, sx + def.w); py1 = Math.max(py1, sy + def.h);
  }
  // Baús e itens presos (techspec-casas §04): peças a mais na mesma lista, com a prio de quem tem altura (z + 1).
  const artesDoBau = cat.extras.bau ?? [];
  const baus = new Set<string>();
  const props = new Map<string, 'bau' | 'preso'>();
  const ocupa: { x: number; y: number; z: number; altura: number; sup: boolean; bloqueia?: boolean }[] = [];
  for (const b of c.baus ?? []) {
    if (![b.x, b.y, b.z, b.giro].every(inteiro)) continue;
    baus.add(`${b.x},${b.y},${b.z}`);
    props.set(`${b.x},${b.y},${b.z}`, 'bau');
    // O baú é um item da mobília (techspec-casa-interface-e-baus §01): o comum grátis ou um comprado, com a arte e a
    // altura do item. Servidor antigo (sem defId): o baú comum.
    const tipo = b.defId && ID_OK.test(b.defId) && ehMobilia(b.defId) ? b.defId : BAU_COMUM;
    if (ehMobilia(tipo)) {
      const altura = alturaDe(tipo);
      ocupa.push({ x: b.x, y: b.y, z: b.z, altura, sup: true, bloqueia: true });
      const zBase = peAbaixoNa(m, c.ox + m.andares.x0, c.oy + m.andares.y0, b.x, b.y, b.z) ?? b.z;
      pecas.push({ x: b.x, y: b.y, z: b.z, def: ICONE_DEF, prio: b.z + altura, porta: false, icone: tipo, giro: b.giro, zBase });
      continue;
    }
    ocupa.push({ x: b.x, y: b.y, z: b.z, altura: ALTURA_DO_BAU, sup: true });
    const d = artesDoBau[b.giro === 1 ? 1 : 0] ?? artesDoBau[0];
    if (d !== undefined) pecas.push({ x: b.x, y: b.y, z: b.z, def: cat.defs[d], prio: b.z + 1, porta: false });
  }
  for (const p of c.presos ?? []) {
    if (![p.x, p.y, p.z].every(inteiro) || !ID_OK.test(p.defId)) continue;
    props.set(`${p.x},${p.y},${p.z}`, 'preso');
    const altura = alturaDe(p.defId);
    ocupa.push({ x: p.x, y: p.y, z: p.z, altura, sup: mobiliaDe(p.defId)?.superficie ?? false, bloqueia: !!p.bloqueia });
    // A mobília (techspec-marceneiro-e-oficios-em-casa §04.3): a arte do giro, com a prio de quem tem altura (z + altura),
    // para a vela em cima da mesa ficar na frente dela. O teto corta pelo piso de onde ela subiu, não pelo alto dela.
    const zBase = peAbaixoNa(m, c.ox + m.andares.x0, c.oy + m.andares.y0, p.x, p.y, p.z) ?? p.z;
    pecas.push({ x: p.x, y: p.y, z: p.z, def: ICONE_DEF, prio: p.z + (ehMobilia(p.defId) ? altura : 1), porta: false, icone: p.defId,
      giro: inteiro(p.giro) ? p.giro : 0, zBase });
  }
  return {
    id: c.id, m, gx: c.ox + m.x0, gy: c.oy + m.y0, ax: c.ox + m.andares.x0, ay: c.oy + m.andares.y0,
    pecas, baus, props, ocupa, papel: c.papel ?? 'visitante',
    placas, nome: typeof c.nome === 'string' ? c.nome : null, dono: typeof c.dono === 'string' ? c.dono : null, aberta: !!c.aberta,
    abrePorta: ['dono', 'codono', 'amigo', 'convidado'].includes(c.papel ?? '') || (!!c.aberta && c.papel !== 'banido'),
    mini, px0, py0, px1, py1
  };
}

/** Para o minimapa (techspec-casas §03.15): as casas de cada casa erguida e se é a sua (dono ou co-dono). */
export function casasNoMinimapa(mapaId: string): readonly { readonly celulas: readonly number[]; readonly minha: boolean }[] {
  if (postas.mapaId !== mapaId) return [];
  return postas.casas.map(c => ({ celulas: c.mini, minha: c.papel === 'dono' || c.papel === 'codono' }));
}

/** As casas do mapa (lista do servidor). Troca a lista inteira; mapa sem casa = lista vazia. */
export async function definirCasasDoMapa(mapaId: string, lista: readonly CasaErguidaNoMapa[]): Promise<void> {
  if (lista.length === 0) {
    postas = { mapaId, casas: [] };
    return;
  }
  const cat = await carregarCasas();
  if (!cat) return;
  postas = { mapaId, casas: lista.map(c => montar(cat, c)).filter((c): c is CasaPosta => c !== null) };
}

/** A casa da planta com a volta de terreno que contém (x, y), e o índice da casa na tabela de andares. */
function naPlanta(mapaId: string, x: number, y: number): { casa: CasaPosta; c: number; i: number; j: number } | null {
  if (postas.mapaId !== mapaId) return null;
  for (const casa of postas.casas) {
    const a = casa.m.andares;
    const i = x - casa.ax, j = y - casa.ay;
    if (i >= 0 && j >= 0 && i < a.w && j < a.h) return { casa, c: j * a.w + i, i, j };
  }
  return null;
}

/** O pé padrão (o mais baixo) da casa em (x, y); `null` fora da planta ou no terreno (vale o terreno do mapa). */
export function zDaCasa(mapaId: string, x: number, y: number): number | null {
  const p = naPlanta(mapaId, x, y);
  if (!p) return null;
  const pes = p.casa.m.andares.n[p.c];
  return pes.length > 0 && pes[0] !== 0 ? pes[0] : null;
}

/** A peça em z está quantos z acima do piso dela? (o pé mais alto em z ou abaixo; 0 fora da planta). */
export function alturaAcimaDoPiso(mapaId: string, x: number, y: number, z: number): number {
  const p = naPlanta(mapaId, x, y);
  if (!p) return 0;
  const pe = peAbaixoNa(p.casa.m, p.casa.ax, p.casa.ay, x, y, z);
  return pe === null ? 0 : Math.max(0, z - pe);
}

/** O pé de (x, y) mais perto de `z` (o andar em que o jogador está); `null` fora da planta ou numa casa sem pé. */
export function peMaisPerto(mapaId: string, x: number, y: number, z: number): number | null {
  const p = naPlanta(mapaId, x, y);
  if (!p) return null;
  const pes = p.casa.m.andares.n[p.c];
  if (pes.length === 0) return null;
  let k = 0;
  for (let q = 1; q < pes.length; q++) if (Math.abs(pes[q] - z) < Math.abs(pes[k] - z)) k = q;
  return pes[k];
}

/** `z` é um pé de (x, y)? `null` fora de qualquer planta (quem decide é o terreno). */
export function peValido(mapaId: string, x: number, y: number, z: number): boolean | null {
  const p = naPlanta(mapaId, x, y);
  if (!p) return null;
  const pes = p.casa.m.andares.n[p.c];
  return pes.length === 0 ? z === 0 : pes.includes(z);
}

/**
 * O passo de (x, y, z) na direção `dir` (0–7, as do jogo): o pé novo, `false` se a casa bloqueia, ou `null` quando a casa
 * não decide (fora da planta, saindo dela, ou numa casa sem pé). Mesma regra do servidor (`CasaMultis.Passo`).
 */
export function passoCasa(mapaId: string, x: number, y: number, z: number, dir: number): number | false | null {
  const p = naPlanta(mapaId, x, y);
  if (!p || dir < 0 || dir > 7) return null;
  const a = p.casa.m.andares;
  const pes = a.n[p.c];
  if (pes.length === 0) return null;
  let k = 0;
  for (let q = 1; q < pes.length; q++) if (Math.abs(pes[q] - z) < Math.abs(pes[k] - z)) k = q;
  const ch = a.t[p.c][k][dir];
  if (ch === '*') return null;
  const ni = p.i + DIRS[dir][0], nj = p.j + DIRS[dir][1];
  if (ch === '-' || ni < 0 || nj < 0 || ni >= a.w || nj >= a.h) return false;
  const destino = a.n[nj * a.w + ni];
  const idx = ch.charCodeAt(0) - 48;
  if (idx < 0 || idx >= destino.length) return false;
  // Baú seguro é móvel fixo: bloqueia o passo no pé dele (o servidor confere igual).
  if (p.casa.baus.has(`${x + DIRS[dir][0]},${y + DIRS[dir][1]},${destino[idx]}`)) return false;
  // A mobília também bloqueia (Renato, 04/11/2026), a não ser a que o console deixou atravessável: a faixa da peça
  // cruza a do corpo, do pé até acima da cabeça (o servidor confere igual, HouseRegistry.PecaBloqueia).
  const tx = x + DIRS[dir][0], ty = y + DIRS[dir][1], tz = destino[idx];
  if (p.casa.ocupa.some(o => o.bloqueia && o.x === tx && o.y === ty && cruzam(o.z, o.altura, tz, ACIMA_DA_CABECA))) return false;
  return destino[idx];
}

/**
 * Empurra as peças das casas na tela para a lista de desenho. `eu` é o jogador local (casa e z do pé): dentro de uma
 * casa, some o que está acima da cabeça; perto de uma porta, a porta abre.
 */
export function pecasDasCasas(
  mapa: MapaCarregado,
  eu: { x: number; y: number; z: number },
  cam: { x: number; y: number },
  vw: number,
  vh: number,
  zoom: number,
  push: (k: number, p: PecaPosta) => void,
  outros: readonly { x: number; y: number; z: number }[] = []
): void {
  // As ferramentas de ofício da cidade (bigorna, roca, alvo): objetos fixos do mapa, desenhados como a mobília.
  if (cidade.mapaId === mapa.id)
    for (const f of cidade.lista) {
      const z = mapa.zEm(f.x, f.y);
      push(f.x + f.y + z + fracDePrio(z + alturaDe(f.defId)), {
        x: f.x, y: f.y, z, def: SEM_DEF, prio: z + alturaDe(f.defId), porta: false, icone: f.defId, giro: f.giro
      });
    }
  if (postas.mapaId !== mapa.id || postas.casas.length === 0) return;
  const left = cam.x - vw / (2 * zoom) - 60, right = cam.x + vw / (2 * zoom) + 60;
  const top = cam.y - vh / (2 * zoom) - 60, bottom = cam.y + vh / (2 * zoom) + 60;
  let dentro: CasaPosta | null = null;
  for (const casa of postas.casas) {
    const i = eu.x - casa.gx, j = eu.y - casa.gy;
    if (i >= 0 && j >= 0 && i < casa.m.w && j < casa.m.h && eu.z > 0) dentro = casa;
  }
  const teto = eu.z + 15;
  for (const casa of postas.casas) {
    if (casa.px1 < left || casa.px0 > right || casa.py1 < top || casa.py0 > bottom) continue;
    const corta = dentro === casa ? teto : Infinity;
    for (const p of casa.pecas) {
      if ((p.zBase ?? p.z) >= corta) continue;
      if (p.porta) {
        // A porta abre sozinha quando o jogador chega a uma casa dela (no andar dela) e fecha quando ele se afasta,
        // com o som da porta do UO (techspec-casas §03.14). Só para quem a porta abre; os outros veem a porta fechada.
        const abrir = (casa.abrePorta && pertoDaPorta(p, eu)) || outros.some(o => pertoDaPorta(p, o) && naGradeDa(casa, o));
        if (p.aberta) {
          const k = p.aberta.chave;
          if (abrir !== portasAbertas.has(k)) {
            if (abrir) portasAbertas.add(k);
            else portasAbertas.delete(k);
            somDaPorta(p.aberta.metal, abrir);
          }
          if (abrir) {
            const x = p.x + p.aberta.dx, y = p.y + p.aberta.dy;
            push(x + y + mapa.zEm(x, y) + fracDePrio(p.prio), { ...p, x, y, def: p.aberta.def });
            continue;
          }
        } else if (abrir) continue; // porta sem arte aberta conhecida: some, como antes
      }
      push(p.x + p.y + mapa.zEm(p.x, p.y) + fracDePrio(p.prio), p);
    }
  }
}

export function desenharPecaDeCasa(
  ctx: Ctx2D,
  mapa: MapaCarregado,
  p: PecaPosta,
  cam: { x: number; y: number },
  vw: number,
  vh: number,
  zoom: number
): void {
  if (p.icone) {
    if (ehMobilia(p.icone)) desenharMobilia(ctx, mapa, p.icone, p.giro ?? 0, p.x, p.y, p.z, cam, vw, vh, zoom, 1);
    else desenharIcone(ctx, mapa, p.icone, p.x, p.y, p.z, cam, vw, vh, zoom, 1);
    return;
  }
  const img = catalogo?.imagem;
  if (!img) return;
  const d = p.def;
  const cx = (p.x - p.y) * mapa.tileW / 2;
  const cy = (p.x + p.y) * mapa.tileH / 2 + mapa.tileH - p.z * Z_PX;
  const sx = (cx - cam.x) * zoom + vw / 2 - d.ax * zoom;
  const sy = (cy - cam.y) * zoom + vh / 2 - d.ay * zoom;
  if (sx > vw + 40 || sy > vh + 40 || sx + d.w * zoom < -40 || sy + d.h * zoom < -40) return;
  ctx.drawImage(img, d.x, d.y, d.w, d.h, sx, sy, d.w * zoom, d.h * zoom);
}

/* ================================================================ ícone do item preso (techspec-casas §03.12)
   O ícone da mochila é uma tela de 44×44 com a arte no meio. No chão, vale só a arte: recortada no tamanho natural
   (sem esticar) e com a base perto do meio do losango, como um item do UO no chão. */
const recortes = new Map<string, { img: HTMLImageElement; sx: number; sy: number; w: number; h: number } | null>();

function recorteDe(defId: string) {
  if (recortes.has(defId)) return recortes.get(defId)!;
  const img = iconeDe(defId);
  if (!img) return null;
  let r = { img, sx: 0, sy: 0, w: img.naturalWidth, h: img.naturalHeight };
  try {
    const cv = document.createElement('canvas');
    cv.width = img.naturalWidth;
    cv.height = img.naturalHeight;
    const g = cv.getContext('2d', { willReadFrequently: true });
    if (g) {
      g.drawImage(img, 0, 0);
      const d = g.getImageData(0, 0, cv.width, cv.height).data;
      let x0 = cv.width, y0 = cv.height, x1 = -1, y1 = -1;
      for (let y = 0; y < cv.height; y++)
        for (let x = 0; x < cv.width; x++)
          if (d[(y * cv.width + x) * 4 + 3] > 16) {
            if (x < x0) x0 = x;
            if (x > x1) x1 = x;
            if (y < y0) y0 = y;
            if (y > y1) y1 = y;
          }
      if (x1 >= x0 && y1 >= y0) r = { img, sx: x0, sy: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
    }
  } catch {
    /* sem leitura de pixels: a tela inteira */
  }
  recortes.set(defId, r);
  return r;
}

function desenharIcone(
  ctx: Ctx2D, mapa: MapaCarregado, defId: string, x: number, y: number, z: number,
  cam: { x: number; y: number }, vw: number, vh: number, zoom: number, alpha: number
): void {
  const r = recorteDe(defId);
  if (!r) return;
  const escala = Math.min(1, 34 / Math.max(r.w, r.h)) * zoom;
  const cx = ((x - y) * mapa.tileW / 2 - cam.x) * zoom + vw / 2;
  const base = ((x + y) * mapa.tileH / 2 + mapa.tileH / 2 + 9 - z * Z_PX - cam.y) * zoom + vh / 2;
  const w = r.w * escala, h = r.h * escala;
  ctx.save();
  ctx.globalAlpha = 0.28 * alpha;
  ctx.fillStyle = '#000';
  ctx.beginPath();
  ctx.ellipse(cx, base - 1 * zoom, Math.max(6 * zoom, w * 0.42), 3.5 * zoom, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = alpha;
  ctx.drawImage(r.img, r.sx, r.sy, r.w, r.h, cx - w / 2, base - h, w, h);
  ctx.restore();
}

/* ================================================================ mobília (techspec-marceneiro-e-oficios-em-casa §04.3)
   A arte crua do cliente do UO para cada giro (tools/mobilia-catalogo.py), na âncora do UO: o pé da arte no fundo do
   losango, centrada na casa, subindo 4 px por z. */
const artesMobilia = new Map<string, HTMLImageElement>();

function arteMobilia(defId: string, giro: number): HTMLImageElement | null {
  const url = arteNoInstante(defId, giro, performance.now());
  const img = imagemDe(url);
  // Quadro ainda carregando: segura a arte parada do giro, para a peça não piscar.
  return img ?? (url === arteDoGiro(defId, giro) ? null : imagemDe(arteDoGiro(defId, giro)));
}

function imagemDe(url: string): HTMLImageElement | null {
  let img = artesMobilia.get(url);
  if (!img) {
    img = new Image();
    img.decoding = 'async';
    img.src = url;
    artesMobilia.set(url, img);
  }
  return img.complete && img.naturalWidth > 0 ? img : null;
}

function desenharMobilia(
  ctx: Ctx2D, mapa: MapaCarregado, defId: string, giro: number, x: number, y: number, z: number,
  cam: { x: number; y: number }, vw: number, vh: number, zoom: number, alpha: number
): void {
  const img = arteMobilia(defId, giro);
  if (!img) return;
  const cx = ((x - y) * mapa.tileW / 2 - cam.x) * zoom + vw / 2;
  const fundo = ((x + y) * mapa.tileH / 2 + mapa.tileH - z * Z_PX - cam.y) * zoom + vh / 2;
  const w = img.naturalWidth * zoom, h = img.naturalHeight * zoom;
  if (cx + w / 2 < -40 || cx - w / 2 > vw + 40 || fundo < -40 || fundo - h > vh + 40) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.drawImage(img, cx - w / 2, fundo - h, w, h);
  ctx.restore();
}

/** O pé (piso) mais alto em (x, y) que fica em z ou abaixo dele, pela tabela de andares (espelha CasaMultis.PeAbaixo). */
function peAbaixoNa(m: ModeloMulti, ax: number, ay: number, x: number, y: number, z: number): number | null {
  const a = m.andares;
  const i = x - ax, j = y - ay;
  if (i < 1 || j < 1 || i > a.w - 2 || j > a.h - 2) return null;
  let melhor: number | null = null;
  for (const pe of a.n[j * a.w + i]) if (pe <= z && (melhor === null || pe > melhor)) melhor = pe;
  return melhor;
}

/* ================================================================ colocar com o cursor (techspec-casas §03.12)
   Como no protótipo: o fantasma segue o mouse no piso da casa, no andar do jogador (verde onde pode, vermelho onde
   não pode); R gira o baú; Esc ou o botão direito cancela; o clique põe. Quem confere de verdade é o servidor. */
export interface Colocacao {
  readonly casaId: string;
  readonly tipo: 'bau' | 'item';
  readonly defId?: string;
  /** Mover uma peça já posta (§04.2): o lugar dela (não conta como ocupado) e o giro e a altura de partida. */
  readonly movendo?: { readonly x: number; readonly y: number; readonly z: number; readonly giro: number; readonly dz: number };
  /** O giro de partida de uma peça nova (Shift + clique continua com o giro da anterior). */
  readonly giro?: number;
  /** `continuar`: Shift + clique (techspec-casa-interface-e-baus §04): fixa e segue com a mesma peça, se houver. */
  onPor(p: { x: number; y: number; z: number; giro: number }, continuar: boolean): void;
  onCancelar(): void;
  onAviso(msg: string): void;
  /** A altura mudou (para a barra: "Altura n/máx"). */
  onAltura?(dz: number, max: number): void;
}

/** O que está sendo posto: o giro, a altura acima do piso (dz) e se o jogador mexeu nela (senão senta na superfície). */
let colocacao: (Colocacao & { giro: number; dz: number; dzManual: boolean }) | null = null;

/** A maior base acima do piso em que a peça cabe sem o topo passar do muro (§04.2). */
export function alturaMaxima(defId: string | undefined, tipo: 'bau' | 'item'): number {
  return Math.max(0, ALTURA_DO_MURO - alturaDaPeca(defId, tipo));
}
/** A altura em z da peça em colocação: o baú pelo item dele (techspec-casa-interface-e-baus §01); sem item, o antigo. */
const alturaDaPeca = (defId: string | undefined, tipo: 'bau' | 'item') =>
  defId ? alturaDe(defId) : tipo === 'bau' ? ALTURA_DO_BAU : 1;

/** Sobe ou desce a peça (PageUp/PageDown, + e −, os botões ▲▼ da barra). */
export function mudarAlturaColocacao(d: number): void {
  const c = colocacao;
  if (!c) return;
  c.dz = Math.max(0, Math.min(alturaMaxima(c.defId, c.tipo), c.dz + d));
  c.dzManual = true;
  c.onAltura?.(c.dz, alturaMaxima(c.defId, c.tipo));
}
let teclasDaColocacao: ((e: KeyboardEvent) => void) | null = null;

export function iniciarColocacao(c: Colocacao): void {
  pararColocacao(false);
  colocacao = { ...c, giro: c.movendo?.giro ?? c.giro ?? 0, dz: c.movendo?.dz ?? 0, dzManual: !!c.movendo };
  c.onAltura?.(colocacao.dz, alturaMaxima(c.defId, c.tipo));
  teclasDaColocacao = e => {
    if (!colocacao) return;
    const alvo = e.target as HTMLElement | null;
    if (alvo && (alvo.tagName === 'INPUT' || alvo.tagName === 'TEXTAREA' || alvo.isContentEditable)) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopImmediatePropagation();
      pararColocacao(true);
    } else if (e.key === 'r' || e.key === 'R') {
      e.preventDefault();
      e.stopImmediatePropagation();
      girarColocacao();
    } else if (e.key === 'PageUp' || e.key === '+' || e.key === '=') {
      e.preventDefault();
      e.stopImmediatePropagation();
      mudarAlturaColocacao(e.shiftKey ? 5 : 1);
    } else if (e.key === 'PageDown' || e.key === '-' || e.key === '_') {
      e.preventDefault();
      e.stopImmediatePropagation();
      mudarAlturaColocacao(e.shiftKey ? -5 : -1);
    }
  };
  window.addEventListener('keydown', teclasDaColocacao, true);
}

export function pararColocacao(avisar = true): void {
  const c = colocacao;
  colocacao = null;
  if (teclasDaColocacao) window.removeEventListener('keydown', teclasDaColocacao, true);
  teclasDaColocacao = null;
  if (avisar) c?.onCancelar();
}

export const colocacaoAtiva = () => colocacao !== null;

export function girarColocacao(): void {
  if (!colocacao) return;
  if (!colocacao.defId) colocacao.giro = colocacao.giro === 0 ? 1 : 0;
  else colocacao.giro = (colocacao.giro + 1) % girosDe(colocacao.defId);
}

/** A peça que está sendo posta gira? (a mobília e os baús com mais de uma arte). */
export const colocacaoGira = () => !!colocacao && (!colocacao.defId || girosDe(colocacao.defId) > 1);

/**
 * O estado da colocação para a faixa (techspec-casa-interface-e-baus §04): o último lugar avaliado sob o cursor, se dá,
 * o motivo, a altura e em cima de quê. `null` sem colocação ou com o cursor fora do mapa.
 */
export interface EstadoColocacao {
  readonly ok: boolean;
  readonly motivo?: string;
  readonly dz: number;
  readonly max: number;
  readonly emCima: string | null;
  readonly giro: number;
}
let ultimoEstado: EstadoColocacao | null = null;
export const estadoDaColocacao = (): EstadoColocacao | null => (colocacao ? ultimoEstado : null);

/** Dá para pôr em (x, y) no andar `zRef`? O pé escolhido é o da casa mais perto do andar do jogador. */
export function lugarParaColocar(
  mapaId: string, casaId: string, x: number, y: number, zRef: number, eu: { x: number; y: number }
): { ok: boolean; z: number; motivo?: string } {
  const casa = postas.mapaId === mapaId ? postas.casas.find(c => c.id === casaId) : undefined;
  if (!casa) return { ok: false, z: zRef, motivo: 'casa.colocar.fora' };
  const a = casa.m.andares;
  const i = x - casa.ax, j = y - casa.ay;
  if (i < 1 || j < 1 || i > a.w - 2 || j > a.h - 2) return { ok: false, z: zRef, motivo: 'casa.colocar.fora' };
  let z: number | null = null;
  for (const pe of a.n[j * a.w + i])
    if (Math.abs(pe - zRef) <= 6 && (z === null || Math.abs(pe - zRef) < Math.abs(z - zRef))) z = pe;
  if (z === null) return { ok: false, z: zRef, motivo: 'casa.colocar.fora' };
  // A altura (techspec-marceneiro-e-oficios-em-casa §04.2): sem o jogador mexer, a peça senta em cima da superfície mais
  // alta do lugar (mesa, cômoda, baú); o topo nunca passa do alto do muro. Quem confere de verdade é o servidor.
  const c = colocacao;
  const altura = alturaDaPeca(c?.defId, c?.tipo ?? 'item');
  const mov = c?.movendo;
  const aqui = casa.ocupa.filter(o => o.x === x && o.y === y && o.z >= z! && o.z < z! + ALTURA_DO_MURO
    && !(mov && o.x === mov.x && o.y === mov.y && o.z === mov.z));
  let dz = c ? c.dz : 0;
  if (c && !c.dzManual) {
    dz = Math.max(0, ...aqui.filter(o => o.sup).map(o => o.z + o.altura - z!));
    dz = Math.min(dz, alturaMaxima(c.defId, c.tipo));
    if (dz !== c.dz) { c.dz = dz; c.onAltura?.(dz, alturaMaxima(c.defId, c.tipo)); }
  }
  const zz = z + dz;
  if (dz + altura > ALTURA_DO_MURO) return { ok: false, z: zz, motivo: 'casa.colocar.passaMuro' };
  if (aqui.some(o => cruzam(zz, altura, o.z, o.altura))) return { ok: false, z: zz, motivo: 'casa.colocar.ocupado' };
  if (x === eu.x && y === eu.y && dz < ACIMA_DA_CABECA) return { ok: false, z: zz, motivo: 'casa.colocar.voce' };
  return { ok: true, z: zz };
}

export function confirmarColocacao(
  mapaId: string, x: number, y: number, zRef: number, eu: { x: number; y: number }, continuar = false
): void {
  const c = colocacao;
  if (!c) return;
  const v = lugarParaColocar(mapaId, c.casaId, x, y, zRef, eu);
  if (!v.ok) {
    c.onAviso(t(v.motivo ?? 'casa.colocar.fora'));
    return;
  }
  pararColocacao(false);
  c.onPor({ x, y, z: v.z, giro: c.giro }, continuar && !c.movendo);
}

/** A superfície mais alta em (x, y) até o z: o nome dela, para "em cima: Mesa grande" na faixa. */
function superficieEm(casa: CasaPosta, x: number, y: number, z: number): string | null {
  let topo = -Infinity, nome: string | null = null;
  for (const p of casa.pecas) {
    if (!p.icone || p.x !== x || p.y !== y || !mobiliaDe(p.icone)?.superficie) continue;
    const t2 = p.z + alturaDe(p.icone);
    if (t2 === z && t2 > topo) { topo = t2; nome = p.icone; }
  }
  return nome;
}

/** A grade fina dos pisos onde dá para pôr (o andar mais perto de `zRef`), por baixo do fantasma (§04). */
function desenharGradeDaCasa(ctx: Ctx2D, mapa: MapaCarregado, casa: CasaPosta, zRef: number, cam: { x: number; y: number }, vw: number, vh: number, zoom: number) {
  const a = casa.m.andares;
  const hw = mapa.tileW / 2 * zoom, hh = mapa.tileH / 2 * zoom;
  ctx.save();
  ctx.strokeStyle = 'rgba(198,232,106,.22)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let j = 1; j < a.h - 1; j++)
    for (let i = 1; i < a.w - 1; i++) {
      const pe = a.n[j * a.w + i].find(z => Math.abs(z - zRef) <= 6);
      if (pe === undefined) continue;
      const x = casa.ax + i, y = casa.ay + j;
      const gx = ((x - y) * mapa.tileW / 2 - cam.x) * zoom + vw / 2;
      const gy = ((x + y) * mapa.tileH / 2 + mapa.tileH / 2 - pe * Z_PX - cam.y) * zoom + vh / 2;
      if (gx < -hw || gx > vw + hw || gy < -hh || gy > vh + hh) continue;
      ctx.moveTo(gx, gy - hh); ctx.lineTo(gx + hw, gy); ctx.lineTo(gx, gy + hh); ctx.lineTo(gx - hw, gy); ctx.closePath();
    }
  ctx.stroke();
  ctx.restore();
}

/** O losango verde/vermelho e o fantasma do que vai ser posto, na casa sob o cursor. */
export function desenharColocacao(
  ctx: Ctx2D, mapa: MapaCarregado, tile: { x: number; y: number } | null, zRef: number, eu: { x: number; y: number },
  cam: { x: number; y: number }, vw: number, vh: number, zoom: number
): void {
  const c = colocacao;
  if (!c) return;
  const casa = postas.mapaId === mapa.id ? postas.casas.find(k => k.id === c.casaId) : undefined;
  if (casa) desenharGradeDaCasa(ctx, mapa, casa, zRef, cam, vw, vh, zoom);
  if (!tile) { ultimoEstado = null; return; }
  const v = lugarParaColocar(mapa.id, c.casaId, tile.x, tile.y, zRef, eu);
  ultimoEstado = {
    ok: v.ok, motivo: v.motivo, dz: c.dz, max: alturaMaxima(c.defId, c.tipo), giro: c.giro,
    emCima: casa && c.dz > 0 ? superficieEm(casa, tile.x, tile.y, v.z) : null
  };
  const gx = ((tile.x - tile.y) * mapa.tileW / 2 - cam.x) * zoom + vw / 2;
  const dzPx = c.dz * Z_PX * zoom;
  // O losango fica no piso; a peça sobe com o fio de altura (§04.3).
  const gy = ((tile.x + tile.y) * mapa.tileH / 2 + mapa.tileH / 2 - v.z * Z_PX - cam.y) * zoom + vh / 2 + dzPx;
  const hw = mapa.tileW / 2 * zoom, hh = mapa.tileH / 2 * zoom;
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(gx, gy - hh);
  ctx.lineTo(gx + hw, gy);
  ctx.lineTo(gx, gy + hh);
  ctx.lineTo(gx - hw, gy);
  ctx.closePath();
  ctx.fillStyle = v.ok ? 'rgba(127,220,90,.20)' : 'rgba(230,90,80,.20)';
  ctx.fill();
  ctx.strokeStyle = v.ok ? 'rgba(127,220,90,.95)' : 'rgba(230,90,80,.95)';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();
  const alpha = v.ok ? 0.8 : 0.4;
  if (dzPx > 0) {
    ctx.save();
    ctx.setLineDash([3, 3]);
    ctx.strokeStyle = v.ok ? 'rgba(198,232,106,.95)' : 'rgba(230,120,100,.95)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(gx, gy);
    ctx.lineTo(gx, gy - dzPx);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.ellipse(gx, gy - dzPx, 14 * zoom, 7 * zoom, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }
  if (c.defId) {
    if (ehMobilia(c.defId)) desenharMobilia(ctx, mapa, c.defId, c.giro, tile.x, tile.y, v.z, cam, vw, vh, zoom, alpha);
    else desenharIcone(ctx, mapa, c.defId, tile.x, tile.y, v.z, cam, vw, vh, zoom, alpha);
    return;
  }
  const cat = catalogo;
  const d = cat?.extras.bau?.[c.giro === 1 ? 1 : 0] ?? cat?.extras.bau?.[0];
  if (!cat || d === undefined) return;
  const def = cat.defs[d];
  const cx = (tile.x - tile.y) * mapa.tileW / 2;
  const cy = (tile.x + tile.y) * mapa.tileH / 2 + mapa.tileH - v.z * Z_PX;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.drawImage(cat.imagem, def.x, def.y, def.w, def.h,
    (cx - cam.x) * zoom + vw / 2 - def.ax * zoom, (cy - cam.y) * zoom + vh / 2 - def.ay * zoom, def.w * zoom, def.h * zoom);
  ctx.restore();
}

/* ================================================================ clique no baú e no item preso */
/* ================================================================ ferramentas de ofício da cidade
   techspec-marceneiro-e-oficios-em-casa §05.2: a bigorna, a roca e o alvo ao lado dos NPCs (Minoc e Cove). Só ali se
   crafta fora da casa, sem bônus. Quem decide é o servidor; aqui só desenha e trata o clique. */
export interface FerramentaDaCidade {
  readonly oficio: string;
  readonly defId: string;
  readonly x: number;
  readonly y: number;
  readonly giro: number;
  /** Bloqueia o passo (o padrão de toda mobília; o console pode deixar atravessável). */
  readonly bloqueia?: boolean;
}
const SEM_DEF = { x: 0, y: 0, w: 0, h: 0, ax: 0, ay: 0 } as unknown as DefPeca;
let cidade: { mapaId: string; lista: readonly FerramentaDaCidade[] } = { mapaId: '', lista: [] };
let aoClicarNaFerramenta: ((f: FerramentaDaCidade, clientX: number, clientY: number) => boolean) | null = null;

/**
 * As luzes da mobília (Renato, 04/11/2026): a vela, a lanterna, o braseiro… das casas e as ferramentas da cidade acendem
 * à noite como as luzes do mapa (a camada de luz pergunta aqui). A luz nasce na altura da chama (a altura da peça).
 */
export function luzesDaMobilia(mapa: MapaCarregado): readonly LuzNoMapa[] {
  const mapaId = mapa.id;
  const out: LuzNoMapa[] = [];
  const luzDe = (defId: string, x: number, y: number, z: number) => {
    const m = mobiliaDe(defId);
    if (m?.luzId == null) return;
    out.push({ x, y, z, luz: m.luzId, cor: m.cor ?? '#ffb45c', tremula: !!m.tremula, h: 44 + m.altura * Z_PX });
  };
  if (postas.mapaId === mapaId)
    for (const casa of postas.casas)
      for (const p of casa.pecas) if (p.icone) luzDe(p.icone, p.x, p.y, p.z);
  if (cidade.mapaId === mapaId)
    for (const f of cidade.lista) luzDe(f.defId, f.x, f.y, mapa.zEm(f.x, f.y));
  return out;
}

/**
 * O assento em (x, y) no pé `z` (Renato, 04/11/2026): a peça da casa cuja arte está na ChairTable da UO, no mesmo pé
 * (±1, como no cliente). Devolve os dados do assento e a faixa da peça, para o corpo ser desenhado na frente dela.
 */
export function assentoEm(mapaId: string, x: number, y: number, z: number):
  { readonly dados: readonly number[]; readonly z: number; readonly altura: number } | null {
  const p = naPlanta(mapaId, x, y);
  if (!p) return null;
  for (const peca of p.casa.pecas) {
    if (!peca.icone || peca.x !== x || peca.y !== y || Math.abs(peca.z - z) > 1) continue;
    const m = mobiliaDe(peca.icone);
    const dados = m?.assento?.[giroValido(peca.icone, peca.giro ?? 0)] ?? null;
    if (dados) return { dados, z: peca.z, altura: m!.altura };
  }
  return null;
}

export function definirFerramentasDaCidade(mapaId: string, lista: readonly FerramentaDaCidade[]): void {
  cidade = { mapaId, lista };
}

/** A ferramenta da cidade em (x, y) bloqueia o passo? (o padrão; o console pode deixar atravessável). */
export function ferramentaBloqueiaEm(mapaId: string, x: number, y: number): boolean {
  return cidade.mapaId === mapaId && cidade.lista.some(f => f.x === x && f.y === y && f.bloqueia !== false);
}

export function ferramentasDaCidade(mapaId: string): readonly FerramentaDaCidade[] {
  return cidade.mapaId === mapaId ? cidade.lista : [];
}

/** Quem trata o clique numa ferramenta da cidade (a tela do mundo abre o ofício dela). `null` desliga. */
export function definirCliqueNaFerramenta(fn: typeof aoClicarNaFerramenta): void {
  aoClicarNaFerramenta = fn;
}

export type CliqueNaCasa = (
  alvo: { casaId: string; tipo: 'bau' | 'preso'; x: number; y: number; z: number }, clientX: number, clientY: number
) => boolean;
let aoClicarNaCasa: CliqueNaCasa | null = null;

/** Quem trata o clique num baú ou item preso (a tela do mundo). `null` desliga. */
export function definirCliqueNaCasa(fn: CliqueNaCasa | null): void {
  aoClicarNaCasa = fn;
}

/** O motor pergunta antes de andar: tem baú ou item preso em (x, y) perto do andar `zRef`? `true` = tratado. */
/** A placa em (x, y) (techspec-casa-interface-e-baus §09): a casa dela, para o rótulo e a janela da placa. */
export interface PlacaDaCasa {
  readonly casaId: string;
  readonly nome: string | null;
  readonly dono: string | null;
  readonly aberta: boolean;
  readonly papel: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
}
export function placaEm(mapaId: string, x: number, y: number): PlacaDaCasa | null {
  if (postas.mapaId !== mapaId) return null;
  for (const casa of postas.casas) {
    const p = casa.placas.find(q => q.x === x && q.y === y);
    if (p) return { casaId: casa.id, nome: casa.nome, dono: casa.dono, aberta: casa.aberta, papel: casa.papel, x: p.x, y: p.y, z: p.z };
  }
  return null;
}
/**
 * A placa debaixo do ponto da tela (sx, sy): a arte dela pendurada no poste, não o tile do chão (o rótulo do UO também
 * abre clicando na arte). A caixa da arte com 4 px de folga.
 */
export function placaNaTela(
  mapa: MapaCarregado, sx: number, sy: number, cam: { x: number; y: number }, vw: number, vh: number, zoom: number
): PlacaDaCasa | null {
  if (postas.mapaId !== mapa.id) return null;
  for (const casa of postas.casas) {
    for (const p of casa.placas) {
      const d = p.def;
      const cx = (p.x - p.y) * mapa.tileW / 2;
      const cy = (p.x + p.y) * mapa.tileH / 2 + mapa.tileH - p.z * Z_PX;
      const x0 = (cx - cam.x) * zoom + vw / 2 - d.ax * zoom, y0 = (cy - cam.y) * zoom + vh / 2 - d.ay * zoom;
      if (sx >= x0 - 4 && sx <= x0 + d.w * zoom + 4 && sy >= y0 - 4 && sy <= y0 + d.h * zoom + 4)
        return { casaId: casa.id, nome: casa.nome, dono: casa.dono, aberta: casa.aberta, papel: casa.papel, x: p.x, y: p.y, z: p.z };
    }
  }
  return null;
}
let aoClicarNaPlaca: ((p: PlacaDaCasa, clientX: number, clientY: number) => void) | null = null;
export function definirCliqueNaPlaca(fn: typeof aoClicarNaPlaca): void {
  aoClicarNaPlaca = fn;
}
/** O clique na tela acertou a arte de uma placa? Abre a placa e devolve true (o clique não anda). */
export function cliqueNaPlaca(
  mapa: MapaCarregado, sx: number, sy: number, cam: { x: number; y: number }, vw: number, vh: number, zoom: number,
  clientX: number, clientY: number
): boolean {
  if (!aoClicarNaPlaca) return false;
  const p = placaNaTela(mapa, sx, sy, cam, vw, vh, zoom);
  if (!p) return false;
  aoClicarNaPlaca(p, clientX, clientY);
  return true;
}

export function cliqueNaCasa(mapaId: string, x: number, y: number, zRef: number, clientX: number, clientY: number): boolean {
  // A placa (§09): de fora ou de dentro, qualquer um; o clique não anda.
  const placa = aoClicarNaPlaca ? placaEm(mapaId, x, y) : null;
  if (placa) { aoClicarNaPlaca!(placa, clientX, clientY); return true; }
  if (aoClicarNaFerramenta && cidade.mapaId === mapaId) {
    const f = cidade.lista.find(t => t.x === x && t.y === y);
    if (f && aoClicarNaFerramenta(f, clientX, clientY)) return true;
  }
  if (!aoClicarNaCasa || postas.mapaId !== mapaId) return false;
  // Várias peças no mesmo lugar (a vela na mesa): o clique pega a mais alta do andar (§04.3).
  for (const casa of postas.casas) {
    let melhor: { tipo: 'bau' | 'preso'; z: number } | null = null;
    for (const [k, tipo] of casa.props) {
      const [px, py, pz] = k.split(',').map(Number);
      if (px === x && py === y && pz >= zRef - 6 && pz < zRef + ALTURA_DO_MURO && (!melhor || pz > melhor.z)) melhor = { tipo, z: pz };
    }
    if (melhor) return aoClicarNaCasa({ casaId: casa.id, tipo: melhor.tipo, x, y, z: melhor.z }, clientX, clientY);
  }
  return false;
}

/** A casa cuja planta (com a volta de terreno) contém (x, y), e o papel do jogador nela. Fora de qualquer casa, `null`. */
export function casaDoPonto(mapaId: string, x: number, y: number):
  { id: string; papel: string; nome: string | null; dono: string | null; aberta: boolean } | null {
  const p = naPlanta(mapaId, x, y);
  return p ? { id: p.casa.id, papel: p.casa.papel, nome: p.casa.nome, dono: p.casa.dono, aberta: p.casa.aberta } : null;
}

/* ================================================================ mostrar na casa (techspec-casa-interface-e-baus §07)
   O ◎ de Gerenciar: um pulso dourado no lugar da peça por 3 s, desenhado por cima das peças. */
let destaque: { mapaId: string; x: number; y: number; z: number; ate: number } | null = null;
export function destacarNaCasa(mapaId: string, x: number, y: number, z: number): void {
  destaque = { mapaId, x, y, z, ate: performance.now() + 3000 };
}
export function desenharDestaque(ctx: Ctx2D, mapa: MapaCarregado, cam: { x: number; y: number }, vw: number, vh: number, zoom: number): void {
  const d = destaque;
  if (!d || d.mapaId !== mapa.id) return;
  const agora = performance.now();
  if (agora > d.ate) { destaque = null; return; }
  const fase = ((d.ate - agora) % 1000) / 1000;
  const gx = ((d.x - d.y) * mapa.tileW / 2 - cam.x) * zoom + vw / 2;
  const gy = ((d.x + d.y) * mapa.tileH / 2 + mapa.tileH / 2 - d.z * Z_PX - cam.y) * zoom + vh / 2;
  const k = 1.6 - fase;
  ctx.save();
  ctx.strokeStyle = `rgba(230,196,82,${0.25 + fase * 0.75})`;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.ellipse(gx, gy, mapa.tileW / 2 * zoom * k, mapa.tileH / 2 * zoom * k, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/* ================================================================ portas (techspec-casas §03.14) */
const portasAbertas = new Set<string>();

/** A uma casa da porta, no andar dela. */
function pertoDaPorta(p: PecaPosta, q: { x: number; y: number; z: number }): boolean {
  return Math.abs(p.x - q.x) <= 1 && Math.abs(p.y - q.y) <= 1 && Math.abs(p.z - q.z) <= 8;
}

/**
 * O outro jogador está na planta do multi (a porta é dela). Lá dentro só fica quem o servidor deixa entrar, então a
 * porta abre para ele sem o cliente saber o papel dele: chegando de fora, abre quando ele pisa na porta.
 */
function naGradeDa(casa: CasaPosta, q: { x: number; y: number }): boolean {
  const i = q.x - casa.gx, j = q.y - casa.gy;
  return i >= 0 && j >= 0 && i < casa.m.w && j < casa.m.h;
}

function somDaPorta(metal: boolean, abrir: boolean): void {
  const id = metal ? (abrir ? PORTA.metalAbre : PORTA.metalFecha) : (abrir ? PORTA.madeiraAbre : PORTA.madeiraFecha);
  tocarUo(id, 'ambiente', { chave: `porta:${id}`, intervaloMs: 150 });
}
